import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

function formatError(detail) {
  if (detail == null) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const inputClass = 'w-full px-4 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring';

export default function Register() {
  const { register: doRegister } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const fn = firstName.trim();
    const ln = lastName.trim();
    // Defensive client-side guard — also enforced on the server. We
    // do not concatenate name; backend derives `name` itself.
    if (!fn || !ln) {
      setError('Voornaam en familienaam zijn verplicht.');
      return;
    }
    setLoading(true);
    try {
      await doRegister({ email, password, firstName: fn, lastName: ln });
      navigate('/onboarding');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="register-page">
      <div className="w-full max-w-sm">
        <h1 className="font-serif text-3xl font-light text-center mb-8">Registreren</h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div data-testid="register-error" className="text-sm text-destructive bg-destructive/10 px-4 py-2.5 rounded-lg">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Voornaam</label>
              <input
                data-testid="register-first-name"
                type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required
                autoComplete="given-name"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Familienaam</label>
              <input
                data-testid="register-last-name"
                type="text" value={lastName} onChange={e => setLastName(e.target.value)} required
                autoComplete="family-name"
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">E-mail</label>
            <input
              data-testid="register-email"
              type="email" value={email} onChange={e => setEmail(e.target.value)} required
              autoComplete="email"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Wachtwoord</label>
            <div className="relative">
              <input
                data-testid="register-password"
                type={showPassword ? 'text' : 'password'}
                value={password} onChange={e => setPassword(e.target.value)} required minLength={8}
                autoComplete="new-password"
                className={`${inputClass} pr-10`}
              />
              <button
                type="button"
                data-testid="register-password-toggle"
                aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                aria-pressed={showPassword}
                onClick={() => setShowPassword(s => !s)}
                className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Minimaal 8 tekens.</p>
          </div>
          <button
            data-testid="register-submit"
            type="submit" disabled={loading}
            className="w-full bg-primary text-primary-foreground rounded-full py-3 font-semibold hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50"
          >
            {loading ? 'Bezig...' : 'Registreren'}
          </button>
        </form>
        <p className="text-center text-sm text-muted-foreground mt-6">
          Al een account? <Link to="/login" className="text-primary font-medium hover:underline">Inloggen</Link>
        </p>
      </div>
    </div>
  );
}
